import { useState, useRef, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";

export interface TranscriptItem {
  timestamp: string;
  orig: string;
  tran: string;
  isMic?: boolean;
}

export type AppMode = "traductor" | "minuta";
export type RecordingStatus = "idle" | "connecting" | "connected" | "disconnected" | "error";

interface UseTranslatorReturn {
  // State
  appMode: AppMode;
  recording: boolean;
  voiceEnabled: boolean;
  pipActive: boolean;
  status: RecordingStatus;
  statusText: string;
  srcLang: string;
  tgtLang: string;
  meetingTranscript: TranscriptItem[];
  currentMeetingId: string | null;
  subtitleOrig: string;
  subtitleTran: string;
  settings: {
    deepgramApiKey: string;
    translatorServiceUrl: string;
    summaryServiceUrl: string;
    userServiceUrl: string;
  };
  // Setters
  setAppMode: (mode: AppMode) => void;
  setVoiceEnabled: (v: boolean) => void;
  setSrcLang: (lang: string) => void;
  setTgtLang: (lang: string) => void;
  // Actions
  toggleRecording: (openSummary: () => void) => void;
  togglePip: () => void;
  saveSettings: (s: {
    deepgramApiKey: string;
    translatorServiceUrl: string;
    summaryServiceUrl: string;
    userServiceUrl: string;
  }) => void;
  selectHistoryItem: (item: TranscriptItem) => void;
  speakTranslation: (text: string, langCode: string, force?: boolean) => void;
  getStatusColorClass: () => string;
  getLanguageLabel: (code: string) => string;
}

export function useTranslator(): UseTranslatorReturn {
  const [appMode, setAppMode] = useState<AppMode>("traductor");
  const [recording, setRecording] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [pipActive, setPipActive] = useState(false);
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [statusText, setStatusText] = useState("Idle");
  const [srcLang, setSrcLang] = useState("auto");
  const [tgtLang, setTgtLang] = useState("es");
  const [meetingTranscript, setMeetingTranscript] = useState<TranscriptItem[]>([]);
  const [currentMeetingId, setCurrentMeetingId] = useState<string | null>(null);
  const [subtitleOrig, setSubtitleOrig] = useState("");
  const [subtitleTran, setSubtitleTran] = useState("");
  const [settings, setSettings] = useState({
    deepgramApiKey: process.env.BUN_PUBLIC_DEEPGRAM_API_KEY || "",
    translatorServiceUrl: process.env.BUN_PUBLIC_TRANSLATOR_SERVICE_URL || "http://localhost:3001",
    summaryServiceUrl: process.env.BUN_PUBLIC_SUMMARY_SERVICE_URL || "http://localhost:3002",
    userServiceUrl: process.env.BUN_PUBLIC_USER_SERVICE_URL || "http://localhost:3003",
  });

  // Mutable refs (don't need re-renders)
  const sysSocketRef = useRef<WebSocket | null>(null);
  const micSocketRef = useRef<WebSocket | null>(null);
  const sysRecorderRef = useRef<MediaRecorder | null>(null);
  const micRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream[]>([]);
  const pipWindowRef = useRef<Window | null>(null);

  // Use a ref for voiceEnabled so audio callbacks always see the latest value
  const voiceEnabledRef = useRef(voiceEnabled);
  useEffect(() => { voiceEnabledRef.current = voiceEnabled; }, [voiceEnabled]);

  // Use a ref for appMode so ws callbacks always see the latest value
  const appModeRef = useRef(appMode);
  useEffect(() => { appModeRef.current = appMode; }, [appMode]);

  // Load settings from localStorage on mount
  useEffect(() => {
    const envApiKey = process.env.BUN_PUBLIC_DEEPGRAM_API_KEY || "";
    const savedApiKey = localStorage.getItem("deepgram_api_key") || envApiKey;

    const envTranslator = process.env.BUN_PUBLIC_TRANSLATOR_SERVICE_URL || "http://localhost:3001";
    const envSummary    = process.env.BUN_PUBLIC_SUMMARY_SERVICE_URL    || "http://localhost:3002";
    const envUser       = process.env.BUN_PUBLIC_USER_SERVICE_URL        || "http://localhost:3003";

    const savedTranslator = localStorage.getItem("translator_service_url") || envTranslator;
    const savedSummary    = localStorage.getItem("summary_service_url")    || envSummary;
    const savedUser       = localStorage.getItem("user_service_url")       || envUser;

    setSettings({
      deepgramApiKey: savedApiKey,
      translatorServiceUrl: savedTranslator,
      summaryServiceUrl: savedSummary,
      userServiceUrl: savedUser,
    });

    return () => {
      stopRecording();
      if (pipWindowRef.current) pipWindowRef.current.close();
    };
  }, []);

  // ── Translation ──────────────────────────────────────────────────────────────
  const translateText = useCallback(async (text: string, src: string, tgt: string): Promise<string> => {
    if (src !== "auto" && src === tgt) return text;
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${src === "auto" ? "auto" : src}&tl=${tgt}&dt=t&q=${encodeURIComponent(text)}`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      return data[0].map((i: any) => i[0]).join("");
    } catch (err) {
      console.error("Translation error:", err);
      return text;
    }
  }, []);

  // ── Text to Speech ───────────────────────────────────────────────────────────
  const speakTranslation = useCallback((text: string, langCode: string, force = false) => {
    if (!force && !voiceEnabledRef.current) return;
    if (!("speechSynthesis" in window)) return;
    if (force) window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const langMap: Record<string, string> = { es: "es-ES", en: "en-US" };
    utterance.lang = langMap[langCode] || langCode;

    const voices = window.speechSynthesis.getVoices();
    const maleKeywords = ["raul", "pablo", "david", "mark", "george", "julio", "jorge", "alex", "daniel", "male"];
    let voice = null;

    if (voices?.length > 0) {
      if (langCode === "es") {
        voice = voices.find((v) => v.lang?.startsWith("es") && v.name?.includes("Google")) || null;
      }
      if (!voice) {
        voice = voices.find((v) =>
          v.lang?.toLowerCase().startsWith(langCode.toLowerCase()) &&
          maleKeywords.some((kw) => v.name?.toLowerCase().includes(kw))
        ) || null;
      }
      if (!voice) {
        voice = voices.find((v) =>
          v.lang?.toLowerCase().startsWith(langCode.toLowerCase()) && v.name?.includes("Google")
        ) || null;
      }
      if (!voice) {
        voice = voices.find((v) => v.lang?.toLowerCase().startsWith(langCode.toLowerCase())) || null;
      }
    }

    if (voice) utterance.voice = voice;
    utterance.rate = 1.1;
    window.speechSynthesis.speak(utterance);
  }, []);

  // ── History ─────────────────────────────────────────────────────────────────
  const addHistory = useCallback((orig: string, tran: string, isMic = false, translatorServiceUrl: string, meetingId: string | null) => {
    const now = new Date();
    const timestamp = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const prefix = isMic ? "🎤 Yo: " : "";
    const newItem: TranscriptItem = { timestamp, orig: prefix + orig, tran: prefix + tran, isMic };
    setMeetingTranscript((prev) => [...prev, newItem]);

    if (meetingId && translatorServiceUrl) {
      getAuthHeaders()
        .then((authHeaders) =>
          fetch(`${translatorServiceUrl}/meeting/transcript`, {
            method: "POST",
            headers: { ...authHeaders, "Content-Type": "application/json" },
            body: JSON.stringify({
              meeting_id: meetingId,
              timestamp,
              original_text: prefix + orig,
              translated_text: prefix + tran,
            }),
          })
        )
        .catch((err) => console.warn("Error saving fragment:", err));
    }
  }, []);

  const selectHistoryItem = useCallback((item: TranscriptItem) => {
    setSubtitleOrig(item.orig);
    setSubtitleTran(item.tran);
    updatePip(item.orig, item.tran);
  }, []);

  // ── PiP ─────────────────────────────────────────────────────────────────────
  const updatePip = (orig: string, tran: string) => {
    if (!pipWindowRef.current) return;
    const doc = pipWindowRef.current.document;
    const w = doc.getElementById("pip-wait");
    const o = doc.getElementById("pip-orig");
    const t = doc.getElementById("pip-tran");
    if (w) w.style.display = "none";
    if (o) { o.style.display = "block"; o.textContent = orig; }
    if (t) { t.style.display = "block"; t.textContent = tran; }
  };

  const openPip = async () => {
    if (!("documentPictureInPicture" in window)) {
      alert("Picture-in-Picture no está disponible en este navegador.\nRequiere Chrome 116+.");
      return;
    }
    try {
      const pipWindow = await (window as any).documentPictureInPicture.requestWindow({ width: 560, height: 180 });
      pipWindowRef.current = pipWindow;
      setPipActive(true);

      pipWindow.document.write(`
        <!DOCTYPE html><html><head><meta charset="UTF-8"/><style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { background:radial-gradient(circle at center,#242429 0%,#121214 100%); color:#f5f5f7; font-family:sans-serif; height:100vh; display:flex; flex-direction:column; justify-content:center; padding:16px 20px; overflow:hidden; border-top:3px solid #a855f7; }
          .pip-header { position:absolute; top:10px; left:20px; right:20px; display:flex; align-items:center; gap:6px; opacity:0.8; }
          .pip-title { font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:#a1a1a6; }
          #pip-orig { font-family:monospace; font-size:11px; color:#6e6e73; margin-top:12px; margin-bottom:6px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          #pip-tran { font-size:24px; font-weight:700; color:#fff; letter-spacing:-.02em; line-height:1.25; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
          #pip-wait { font-size:13px; color:#6e6e73; text-align:center; display:flex; align-items:center; justify-content:center; gap:8px; }
          .pulse { width:6px; height:6px; background:#a855f7; border-radius:50%; display:inline-block; animation:pip-pulse 2s infinite; }
          @keyframes pip-pulse { 0%{box-shadow:0 0 0 0 rgba(168,85,247,.4)} 70%{box-shadow:0 0 0 6px rgba(168,85,247,0)} 100%{box-shadow:0 0 0 0 rgba(168,85,247,0)} }
        </style></head>
        <body>
          <div class="pip-header"><span class="pip-title">Kira </span></div>
          <div id="pip-wait"><span class="pulse"></span>Escuchando audio...</div>
          <div id="pip-orig" style="display:none"></div>
          <div id="pip-tran" style="display:none"></div>
        </body></html>
      `);

      pipWindow.addEventListener("pagehide", () => {
        pipWindowRef.current = null;
        setPipActive(false);
      });
    } catch (e: any) {
      alert("No se pudo abrir la ventana PiP: " + e.message);
    }
  };

  const closePip = () => {
    if (pipWindowRef.current) pipWindowRef.current.close();
    pipWindowRef.current = null;
    setPipActive(false);
  };

  const togglePip = () => { if (pipActive) closePip(); else openPip(); };

  // ── Recording ────────────────────────────────────────────────────────────────
  const stopRecording = useCallback(() => {
    if (sysRecorderRef.current?.state !== "inactive") { try { sysRecorderRef.current?.stop(); } catch (e) {} }
    sysRecorderRef.current = null;
    if (micRecorderRef.current?.state !== "inactive") { try { micRecorderRef.current?.stop(); } catch (e) {} }
    micRecorderRef.current = null;
    try { sysSocketRef.current?.close(); } catch (e) {}
    sysSocketRef.current = null;
    try { micSocketRef.current?.close(); } catch (e) {}
    micSocketRef.current = null;
    streamRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    streamRef.current = [];
    setRecording(false);
    setStatus("idle");
    setStatusText("Idle");
    if ("speechSynthesis" in window) { try { window.speechSynthesis.cancel(); } catch (e) {} }
    if (appModeRef.current === "minuta") {
      // Will be handled by App.tsx via state
    }
  }, []);

  const startRecording = useCallback(async (
    currentSettings: {
      deepgramApiKey: string;
      translatorServiceUrl: string;
      summaryServiceUrl: string;
      userServiceUrl: string;
    },
    currentSrcLang: string,
    currentTgtLang: string,
    currentAppMode: AppMode,
    openSummary: () => void
  ) => {
    if (!currentSettings.deepgramApiKey) {
      alert("Por favor, introduce tu API Key de Deepgram en los ajustes antes de iniciar.");
      return;
    }

    let displayStream: MediaStream | null = null;
    let micStream: MediaStream | null = null;

    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      } catch (micErr) {
        console.warn("Microphone not available:", micErr);
      }
    } catch (e: any) {
      alert("No se pudo iniciar la captura de pantalla: " + e.message);
      return;
    }

    streamRef.current = [displayStream, micStream].filter(Boolean) as MediaStream[];
    setMeetingTranscript([]);
    setSubtitleOrig("");
    setSubtitleTran("");

    const sysUrl = currentSrcLang === "auto"
      ? "wss://api.deepgram.com/v1/listen?model=nova-2&language=multi&smart_format=true"
      : `wss://api.deepgram.com/v1/listen?model=nova-2&language=${currentSrcLang}&smart_format=true`;
    const micUrl = "wss://api.deepgram.com/v1/listen?model=nova-2&language=es&smart_format=true";

    setRecording(true);
    setStatus("connecting");
    setStatusText("Conectando...");

    // Register session in backend
    let meetingId: string | null = null;
    if (currentSettings.translatorServiceUrl) {
      try {
        const authHeaders = await getAuthHeaders();
        const titleStr = `Sesión ${new Date().toLocaleDateString("es-ES")} ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
        const res = await fetch(`${currentSettings.translatorServiceUrl}/meeting`, {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ title: titleStr, source_lang: currentSrcLang, target_lang: currentTgtLang }),
        });
        if (res.ok) {
          const data = await res.json();
          meetingId = data.meeting_id;
          setCurrentMeetingId(data.meeting_id);
        }
      } catch (err) {
        console.warn("Could not register session:", err);
      }
    }

    // System audio WebSocket
    if (displayStream && displayStream.getAudioTracks().length > 0) {
      const ws = new WebSocket(sysUrl, ["token", currentSettings.deepgramApiKey]);
      sysSocketRef.current = ws;

      ws.onopen = () => {
        setStatus("connected");
        setStatusText("Conectado");
        const recorder = new MediaRecorder(displayStream!);
        sysRecorderRef.current = recorder;
        recorder.addEventListener("dataavailable", (e) => {
          if (e.data.size > 0 && ws.readyState === 1) ws.send(e.data);
        });
        recorder.start(250);
      };

      ws.onmessage = async (msg) => {
        const data = JSON.parse(msg.data);
        if (data.type === "Metadata") return;
        const transcript = data.channel?.alternatives[0]?.transcript;
        if (!transcript || !data.is_final) return;

        if (appModeRef.current === "traductor") {
          const translated = await translateText(transcript, currentSrcLang, currentTgtLang);
          setSubtitleOrig(transcript);
          setSubtitleTran(translated);
          updatePip(transcript, translated);
          addHistory(transcript, translated, false, currentSettings.translatorServiceUrl, meetingId);
          speakTranslation(translated, currentTgtLang);
        } else {
          addHistory(transcript, transcript, false, currentSettings.translatorServiceUrl, meetingId);
        }
      };

      ws.onclose = () => { stopRecording(); };
      ws.onerror = () => { stopRecording(); };
    }

    // Microphone WebSocket
    if (micStream && micStream.getAudioTracks().length > 0) {
      const ws = new WebSocket(micUrl, ["token", currentSettings.deepgramApiKey]);
      micSocketRef.current = ws;

      ws.onopen = () => {
        const recorder = new MediaRecorder(micStream!);
        micRecorderRef.current = recorder;
        recorder.addEventListener("dataavailable", (e) => {
          if (e.data.size > 0 && ws.readyState === 1) ws.send(e.data);
        });
        recorder.start(250);
      };

      ws.onmessage = async (msg) => {
        const data = JSON.parse(msg.data);
        if (data.type === "Metadata") return;
        const transcript = data.channel?.alternatives[0]?.transcript;
        if (!transcript || !data.is_final) return;
        addHistory(transcript, transcript, true, currentSettings.translatorServiceUrl, meetingId);
      };
    }

    // Auto-open summary when stopping in "minuta" mode
    const originalStop = stopRecording;
    // This is handled by App.tsx watching recording state changes via onStopCallback
  }, [translateText, addHistory, speakTranslation, stopRecording]);

  const toggleRecording = useCallback(
    (openSummary: () => void) => {
      if (recording) {
        stopRecording();
        if (appModeRef.current === "minuta") openSummary();
      } else {
        startRecording(settings, srcLang, tgtLang, appMode, openSummary);
      }
    },
    [recording, settings, srcLang, tgtLang, appMode, startRecording, stopRecording]
  );

  // ── Utilities ────────────────────────────────────────────────────────────────
  const saveSettings = useCallback((newSettings: {
    deepgramApiKey: string;
    translatorServiceUrl: string;
    summaryServiceUrl: string;
    userServiceUrl: string;
  }) => {
    setSettings(newSettings);
    localStorage.setItem("deepgram_api_key", newSettings.deepgramApiKey);
    localStorage.setItem("translator_service_url", newSettings.translatorServiceUrl);
    localStorage.setItem("summary_service_url", newSettings.summaryServiceUrl);
    localStorage.setItem("user_service_url", newSettings.userServiceUrl);
  }, []);

  const getStatusColorClass = useCallback(() => {
    switch (status) {
      case "connecting": return "border-yellow-500/30 text-yellow-400 bg-yellow-500/5 before:bg-yellow-400";
      case "connected": return "border-emerald-500/30 text-emerald-400 bg-emerald-500/5 before:bg-emerald-400 before:shadow-[0_0_6px_#10b981] before:animate-pulse";
      case "error": return "border-rose-500/30 text-rose-400 bg-rose-500/5 before:bg-rose-400";
      default: return "border-white/10 text-white/40 bg-white/5 before:bg-white/30";
    }
  }, [status]);

  const getLanguageLabel = useCallback((code: string) => {
    const labels: Record<string, string> = { auto: "✨ Auto-detectar", en: "English", es: "Español" };
    return labels[code] || code;
  }, []);

  return {
    appMode, recording, voiceEnabled, pipActive, status, statusText,
    srcLang, tgtLang, meetingTranscript, currentMeetingId,
    subtitleOrig, subtitleTran, settings,
    setAppMode, setVoiceEnabled, setSrcLang, setTgtLang,
    toggleRecording, togglePip, saveSettings,
    selectHistoryItem, speakTranslation,
    getStatusColorClass, getLanguageLabel,
  };
}
